import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../../components/ui/dropdown-menu";
import { Truck, Calendar as CalendarIcon, Loader2, MoreVertical, CheckCircle, XCircle, Plus, User, Star, Phone, Mail, TrendingUp } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { format, differenceInDays } from "date-fns";
import { it } from "date-fns/locale";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function CompanyBookings() {
  const { user, token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [vehiclesList, setVehiclesList] = useState([]);
  const [customers, setCustomers] = useState({});
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  // Create booking dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Customer details dialog
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingNotes, setRatingNotes] = useState("");
  const [savingRating, setSavingRating] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  const fetchBookings = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = statusFilter ? `?status=${statusFilter}` : "";
      
      const [bookingsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/bookings${params}`, { headers, withCredentials: true }),
        axios.get(`${API}/vehicles?company_id=${user?.company_id}`, { headers, withCredentials: true })
      ]);
      
      setBookings(bookingsRes.data);
      setVehiclesList(vehiclesRes.data);

      // Create vehicle map
      const vehicleMap = {};
      vehiclesRes.data.forEach(v => vehicleMap[v.vehicle_id] = v);
      setVehicles(vehicleMap);

      // Get unique customer IDs from bookings
      const customerIds = [...new Set(bookingsRes.data.map(b => b.customer_id))];
      
      // Fetch customer details and build existing customers list
      if (customerIds.length > 0) {
        try {
          // Try to get all users (admin only) - if fails, we'll use booking data
          const usersRes = await axios.get(`${API}/users`, { headers, withCredentials: true });
          const userMap = {};
          usersRes.data.forEach(u => userMap[u.user_id] = u);
          setCustomers(userMap);
          
          // Filter to only customers who have booked with this company
          const existingCusts = usersRes.data.filter(u => customerIds.includes(u.user_id));
          setExistingCustomers(existingCusts);
        } catch (e) {
          // Company can't access all users, use booking info
          const customerDetails = {};
          customerIds.forEach(id => {
            customerDetails[id] = { user_id: id, name: `Cliente ${id.slice(-6)}`, email: "" };
          });
          setCustomers(customerDetails);
          setExistingCustomers(Object.values(customerDetails));
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/bookings/${bookingId}/status`, { status: newStatus }, { headers, withCredentials: true });
      toast.success(`Prenotazione ${getStatusLabel(newStatus).toLowerCase()}`);
      fetchBookings();
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    }
  };

  const getSelectedVehicle = () => {
    return vehiclesList.find(v => v.vehicle_id === selectedVehicle);
  };

  const calculatePrice = () => {
    const vehicle = getSelectedVehicle();
    if (!startDate || !endDate || !vehicle) return 0;
    
    const days = differenceInDays(endDate, startDate);
    if (days < 1) return vehicle.daily_rate;
    
    if (days >= 7 && vehicle.weekly_rate) {
      const weeks = Math.floor(days / 7);
      const remainingDays = days % 7;
      return (weeks * vehicle.weekly_rate) + (remainingDays * vehicle.daily_rate);
    }
    
    return days * vehicle.daily_rate;
  };

  const handleCreateBooking = async () => {
    if (!selectedCustomer || !selectedVehicle || !startDate || !endDate) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/bookings/company`, {
        customer_id: selectedCustomer,
        vehicle_id: selectedVehicle,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        notes: notes || null
      }, { headers, withCredentials: true });

      toast.success("Prenotazione creata con successo!");
      setShowCreateDialog(false);
      resetForm();
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la creazione");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedCustomer("");
    setSelectedVehicle("");
    setStartDate(null);
    setEndDate(null);
    setNotes("");
  };

  // Fetch customer details with stats
  const fetchCustomerDetails = async (bookingId) => {
    setLoadingCustomer(true);
    setShowCustomerDialog(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/bookings/${bookingId}/customer`, { headers, withCredentials: true });
      setCustomerDetails(res.data);
      setRatingStars(res.data.your_rating || 0);
      setRatingNotes("");
    } catch (error) {
      toast.error("Errore nel caricamento dei dettagli cliente");
      setShowCustomerDialog(false);
    } finally {
      setLoadingCustomer(false);
    }
  };

  // Save customer rating
  const saveRating = async () => {
    if (!customerDetails || ratingStars < 1) {
      toast.error("Seleziona almeno 1 stella");
      return;
    }
    
    setSavingRating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/customers/${customerDetails.customer.user_id}/rate`, {
        stars: ratingStars,
        notes: ratingNotes
      }, { headers, withCredentials: true });
      
      toast.success("Valutazione salvata!");
      setCustomerDetails(prev => ({
        ...prev,
        your_rating: ratingStars
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingRating(false);
    }
  };

  // Star rating component
  const StarRating = ({ value, onChange, readonly = false }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onChange(star)}
            className={`transition-colors ${readonly ? "cursor-default" : "cursor-pointer hover:scale-110"}`}
          >
            <Star
              className={`h-6 w-6 ${star <= value ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout title="Prenotazioni">
      <div className="space-y-6" data-testid="company-bookings">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="Tutti gli stati" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="pending">In attesa</SelectItem>
              <SelectItem value="confirmed">Confermate</SelectItem>
              <SelectItem value="completed">Completate</SelectItem>
              <SelectItem value="cancelled">Cancellate</SelectItem>
            </SelectContent>
          </Select>

          {existingCustomers.length > 0 && vehiclesList.length > 0 && (
            <Button onClick={() => setShowCreateDialog(true)} className="btn-primary py-2" data-testid="create-booking-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuova Prenotazione
            </Button>
          )}
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : bookings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Nessuna prenotazione</h3>
              <p className="text-muted-foreground text-sm">
                {statusFilter ? "Nessuna prenotazione con questo stato" : "Non hai ancora ricevuto prenotazioni"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => {
              const vehicle = vehicles[booking.vehicle_id];
              const customer = customers[booking.customer_id];
              
              return (
                <Card key={booking.booking_id} data-testid={`booking-${booking.booking_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="bg-muted w-20 h-14 flex items-center justify-center">
                          {vehicle?.images?.[0] ? (
                            <img
                              src={vehicle.images[0].startsWith('http') ? vehicle.images[0] : `${process.env.REACT_APP_BACKEND_URL}${vehicle.images[0]}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Truck className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getStatusColor(booking.status)}>
                              {getStatusLabel(booking.status)}
                            </Badge>
                            <span className="text-xs text-muted-foreground font-mono">
                              #{booking.booking_id.slice(-8)}
                            </span>
                          </div>
                          <h3 className="font-bold">
                            {vehicle ? `${vehicle.brand} ${vehicle.model}` : "Veicolo"}
                          </h3>
                          <button 
                            onClick={() => fetchCustomerDetails(booking.booking_id)}
                            className="text-sm text-primary hover:underline flex items-center gap-1 cursor-pointer"
                          >
                            <User className="h-4 w-4" />
                            {customer?.name || "Cliente"} 
                            <span className="text-xs text-muted-foreground">(vedi dettagli)</span>
                          </button>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <CalendarIcon className="h-4 w-4" />
                            {formatDate(booking.start_date)} - {formatDate(booking.end_date)}
                          </p>
                          {booking.notes && (
                            <p className="text-sm text-muted-foreground mt-1">Note: {booking.notes}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground uppercase">Totale</p>
                          <p className="text-xl font-bold font-mono text-primary">
                            {formatCurrency(booking.total_price)}
                          </p>
                        </div>
                        
                        {booking.status === "pending" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`booking-actions-${booking.booking_id}`}>
                                <MoreVertical className="h-5 w-5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => updateStatus(booking.booking_id, "confirmed")}
                                className="text-green-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Conferma
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => updateStatus(booking.booking_id, "cancelled")}
                                className="text-red-600"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Rifiuta
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        
                        {booking.status === "confirmed" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateStatus(booking.booking_id, "completed")}
                            data-testid={`complete-booking-${booking.booking_id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Completa
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Booking Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                Nuova Prenotazione
              </DialogTitle>
              <DialogDescription>
                Crea una prenotazione per un cliente esistente
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Customer Selection - Only existing customers */}
              <div>
                <Label className="input-label">Cliente *</Label>
                <Select value={selectedCustomer || "select"} onValueChange={(v) => setSelectedCustomer(v === "select" ? "" : v)}>
                  <SelectTrigger data-testid="booking-customer">
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select" disabled>Seleziona cliente</SelectItem>
                    {existingCustomers.map((c) => (
                      <SelectItem key={c.user_id} value={c.user_id}>
                        {c.name} {c.email && `(${c.email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Solo clienti che hanno già prenotato con la tua azienda
                </p>
              </div>

              {/* Vehicle Selection */}
              <div>
                <Label className="input-label">Veicolo *</Label>
                <Select value={selectedVehicle || "select"} onValueChange={(v) => setSelectedVehicle(v === "select" ? "" : v)}>
                  <SelectTrigger data-testid="booking-vehicle">
                    <SelectValue placeholder="Seleziona veicolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select" disabled>Seleziona veicolo</SelectItem>
                    {vehiclesList.filter(v => v.is_available !== false).map((v) => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                        {v.brand} {v.model} - {formatCurrency(v.daily_rate)}/giorno
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Data Inizio *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="booking-start-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "dd/MM/yyyy", { locale: it }) : "Seleziona"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        disabled={(date) => date < new Date()}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="input-label">Data Fine *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="booking-end-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "dd/MM/yyyy", { locale: it }) : "Seleziona"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={(date) => date < (startDate || new Date())}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="input-label">Note (opzionale)</Label>
                <Textarea
                  placeholder="Aggiungi eventuali note..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="booking-notes"
                />
              </div>

              {/* Price Summary */}
              {startDate && endDate && selectedVehicle && (
                <div className="bg-muted/50 p-4 border border-border">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-muted-foreground">Durata</span>
                    <span className="font-mono">{differenceInDays(endDate, startDate) || 1} giorni</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Totale Stimato</span>
                    <span className="text-primary font-mono">{formatCurrency(calculatePrice())}</span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                Annulla
              </Button>
              <Button
                className="btn-primary py-2"
                onClick={handleCreateBooking}
                disabled={submitting || !selectedCustomer || !selectedVehicle || !startDate || !endDate}
                data-testid="confirm-create-booking"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  "Crea Prenotazione"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Customer Details Dialog */}
        <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Dettagli Cliente
              </DialogTitle>
              <DialogDescription>
                Informazioni e statistiche di affidabilità
              </DialogDescription>
            </DialogHeader>

            {loadingCustomer ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : customerDetails ? (
              <div className="space-y-6">
                {/* Customer Info */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-lg">{customerDetails.customer.name}</h4>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${customerDetails.customer.email}`} className="hover:text-primary">
                      {customerDetails.customer.email}
                    </a>
                  </div>
                  
                  {customerDetails.customer.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <a href={`tel:${customerDetails.customer.phone}`} className="hover:text-primary">
                        {customerDetails.customer.phone}
                      </a>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <h5 className="font-medium flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Statistiche Affidabilità
                  </h5>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-background rounded border">
                      <p className="text-2xl font-bold text-primary">{customerDetails.stats.total_bookings}</p>
                      <p className="text-xs text-muted-foreground">Prenotazioni Totali</p>
                    </div>
                    <div className="text-center p-3 bg-background rounded border">
                      <p className="text-2xl font-bold text-green-600">{customerDetails.stats.completed_bookings}</p>
                      <p className="text-xs text-muted-foreground">Completate</p>
                    </div>
                  </div>
                  
                  <div className="text-center p-3 bg-primary/10 rounded border border-primary/20">
                    <p className="text-2xl font-bold">{customerDetails.stats.with_your_company}</p>
                    <p className="text-xs text-muted-foreground">Con la tua azienda</p>
                  </div>
                </div>

                {/* Rating Section */}
                <div className="border-t pt-4 space-y-3">
                  <h5 className="font-medium flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    La tua valutazione
                  </h5>
                  
                  {customerDetails.your_rating ? (
                    <div className="flex items-center gap-2">
                      <StarRating value={customerDetails.your_rating} readonly />
                      <span className="text-sm text-muted-foreground">
                        ({customerDetails.your_rating}/5)
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Non hai ancora valutato questo cliente
                      </p>
                      <div className="flex items-center gap-3">
                        <StarRating value={ratingStars} onChange={setRatingStars} />
                        {ratingStars > 0 && (
                          <span className="text-sm font-medium">{ratingStars}/5</span>
                        )}
                      </div>
                      <Textarea
                        placeholder="Note sulla valutazione (opzionale)"
                        value={ratingNotes}
                        onChange={(e) => setRatingNotes(e.target.value)}
                        className="text-sm"
                        rows={2}
                      />
                      <Button
                        onClick={saveRating}
                        disabled={ratingStars < 1 || savingRating}
                        className="btn-primary py-2 w-full"
                      >
                        {savingRating ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Salvataggio...
                          </>
                        ) : (
                          "Salva Valutazione"
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
